import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface AudienceMember {
  id: string;
  name: string;
  email: string;
  phone?: string;
  dob?: string;
  gender?: string;
  address?: string;
  postalCode?: string;
  segment: string;
  tags: string[];
  lastActivity: string;
  engageScore: number | undefined;
  status: string;
  segments?: string[];
  customFields?: {
    [key: string]: any;
  };
}

export interface SegmentConfig {
  name: string;
  fields: string[];
  fieldTypes: { [key: string]: string }; // New property to store input types
}

interface AudienceState {
  members: AudienceMember[];
  segments: SegmentConfig[];
}

const initialState: AudienceState = {
  members: [
    {
      id: '1',
      name: 'Amy Denham',
      email: 'amy@example.com',
      phone: '+1 202-555-0198',
      dob: '2005-07-12',
      gender: 'Female',
      address: "12 Rue de la République, N'Djamena, Chad",
      postalCode: '2351',
      segment: 'New Subscriber',
      tags: ['Entertainment', 'Tech'],
      lastActivity: '3 days ago',
      engageScore: 97,
      status: 'Subscribed',
      segments: ['seg1', 'seg2'],
    },
    {
      id: '2',
      name: 'John Smith',
      email: 'john@example.com',
      phone: '+1 202-555-0147',
      dob: '1990-11-02',
      gender: 'Male',
      address: '45 Oxford Street, London, UK',
      postalCode: 'W1D 2DZ',
      segment: 'New Subscriber',
      tags: ['Entertainment'],
      lastActivity: '1 week ago',
      engageScore: 45,
      status: 'Subscribed',
      segments: ['seg1'],
    },
  ],
  segments: [
    {
      name: 'New Subscriber',
      fields: ['Full Name', 'Email', 'Contact', 'Gender', 'DOB', 'Location'],
      fieldTypes: {
        'Full Name': 'Text',
        Email: 'Text',
        Contact: 'Text',
        Gender: 'Dropdown',
        DOB: 'Calendar',
        Location: 'Text',
      },
    },
    {
      name: 'Engaged User',
      fields: ['Full Name', 'Email', 'Contact', 'Gender'],
      fieldTypes: {
        'Full Name': 'Text',
        Email: 'Text',
        Contact: 'Text',
        Gender: 'Dropdown',
      },
    },
    {
      name: 'Loyal User',
      fields: ['Full Name', 'Email', 'Contact', 'DOB', 'Location'],
      fieldTypes: {
        'Full Name': 'Text',
        Email: 'Text',
        Contact: 'Text',
        DOB: 'Calendar',
        Location: 'Text',
      },
    },
  ],
};

const audienceSlice = createSlice({
  name: 'audience',
  initialState,
  reducers: {
    addAudience: (state, action: PayloadAction<AudienceMember>) => {
      state.members.push(action.payload);
    },
    addSegment: (state, action: PayloadAction<SegmentConfig>) => {
      const existingIndex = state.segments.findIndex(
        (s) => s.name === action.payload.name
      );
      if (existingIndex === -1) {
        state.segments.push(action.payload);
      } else {
        state.segments[existingIndex] = {
          ...state.segments[existingIndex],
          ...action.payload,
          fieldTypes: {
            ...state.segments[existingIndex].fieldTypes,
            ...action.payload.fieldTypes,
          },
        };
      }
    },
    updateAllSegments: (state, action: PayloadAction<string>) => {
      state.members = state.members.map((m) => ({
        ...m,
        segment: action.payload,
      }));
    },
    updateSegmentName: (
      state,
      action: PayloadAction<{ oldName: string; newName: string }>
    ) => {
      const { oldName, newName } = action.payload;
      const index = state.segments.findIndex((s) => s.name === oldName);
      if (index !== -1) {
        state.segments[index].name = newName;
      }
      state.members = state.members.map((m) =>
        m.segment === oldName ? { ...m, segment: newName } : m
      );
    },
    updateSegment: (state, action: PayloadAction<SegmentConfig>) => {
      const index = state.segments.findIndex(
        (s) => s.name === action.payload.name
      );
      if (index !== -1) {
        state.segments[index] = action.payload;
      }
    },
    updateSegmentsForIds: (
      state,
      action: { payload: { ids: string[]; segment: string } }
    ) => {
      const { ids, segment } = action.payload;
      state.members = state.members.map((m) =>
        ids.includes(m.id) ? { ...m, segment } : m
      );
    },
    updateAudience: (state, action: PayloadAction<AudienceMember>) => {
      const index = state.members.findIndex((m) => m.id === action.payload.id);
      if (index !== -1) {
        state.members[index] = action.payload;
      }
    },
    updateContactSegments: (
      state,
      action: PayloadAction<{ id: string; segments: string[] }>
    ) => {
      const { id, segments } = action.payload;
      const index = state.members.findIndex((m) => m.id === id);
      if (index !== -1) {
        state.members[index].segments = segments;
      }
    },
  },
});

export const {
  addAudience,
  addSegment,
  updateAllSegments,
  updateSegmentName,
  updateSegment,
  updateSegmentsForIds,
  updateAudience,
  updateContactSegments,
} = audienceSlice.actions;
export default audienceSlice.reducer;
